import {
  AlbumRounded,
  CloudDownloadRounded,
  HomeRounded,
} from '@mui/icons-material';
import {
  internetCategory,
  resourceCategory,
  resourceEduCode,
  resourceVersion,
} from '~/api/oneBoxV3/oneBoxV3.type';
import { TInternetSearchCondition } from '~/store/internetResource/internetResource.type';
import { TSearchCondition } from '~/store/resource/resource.type';
import { SIDEBAR } from '~/types/enum';

/**
 * 網路資源查詢的頁筆數。
 *
 * getInternetResources 的 search 沒有帶 resources1／resources2，回傳的是該學年／學制／
 * 科目／年級底下「所有類型」的網路資源，畫面才依 resources2 過濾（例如教學PPT 頁只留
 * 網頁PPT）。若沿用側欄的頁筆數（教學PPT 為 4），網頁PPT 會被 GGB 等其他網路資源擠出
 * 第一頁而整個年級都不顯示，因此網路資源一律用固定的大頁筆數查詢。
 */
export const INTERNET_RESOURCE_PAGE_SIZE = 100;

export const resolveInternetPageSize = (resources1?: string | null) =>
  (resources1 as SIDEBAR) === SIDEBAR.latestNews
    ? 5
    : INTERNET_RESOURCE_PAGE_SIZE;

export const routePath = {
  home: '/',
  file: '/files',
  resource: '/resource',
  history: '/history',
  login: '/login',
  admin: '/admin',
  adminLogin: '/admin/login',
  latestNews: '/latestnews',
  pdf: '/pdf',
} as const;

export const INTERNET = [
  'QQ 快答',
  'Kahoot!',
  'Quizizz',
  'Quizlet',
  'Wordwall',
  'Coggle',
];

// export const sideBarCol = {
//   [SIDEBAR.ebook]: '電子書',
//   [SIDEBAR.tiku]: '題庫光碟',
//   [SIDEBAR.media]: '多媒體',
//   [SIDEBAR.test]: '卷類資源',
//   [SIDEBAR.ppt]: '教學 PPT',
//   [SIDEBAR.resource]: '教材資源',
//   [SIDEBAR.supplement]: '備課資源',
//   [SIDEBAR.schedule]: '授課教案',
//   [SIDEBAR.plan]: '課程計畫',
//   [SIDEBAR.internet]: '網路資源',
// };

export const subjectsSortList = {
  E: ['國語', '數學', '生活', '自然', '社會', '健體', '綜合', '藝文'],
  J: [
    '國文',
    '英語',
    '數學',
    '自然',
    '地理',
    '歷史',
    '公民',
    '科技',
    '健體',
    '綜合',
  ],
  H: [
    '國文',
    '數學',
    '生物',
    '物理',
    '化學',
    '地球科學',
    '地理',
    '歷史',
    '公民與社會',
  ],
};

export const navigator = [
  {
    onlyElectron: true,
    icon: <AlbumRounded />,
    text: '我的光碟盒',
    path: routePath.file,
  },
  {
    onlyElectron: true,
    icon: <HomeRounded />,
    text: '取得資源',
    path: routePath.resource,
  },
  {
    onlyElectron: true,
    icon: <CloudDownloadRounded />,
    text: '下載紀錄',
    path: '/history',
  },
];

export const loginErrorMessage = {
  emptyAccount: '請輸入帳號',
  emptyPassword: '請輸入密碼',
  invalidFormat: '輸入格式錯誤',
  wrongInput: '登入失敗，帳號或密碼錯誤',
  unknown: '登入失敗，發生異常錯誤',
  permissionDenied: '權限不足，登入失敗',
};

export const gradeMap = (grade: string) => {
  const typeName: { [grade: string]: string } = {
    '1': '1年級',
    '2': '2年級',
    '3': '3年級',
    '4': '4年級',
    '5': '5年級',
    '6': '6年級',
  };
  return typeName[`${grade}`] || '-';
};

export const getHeader = (id: keyof TSearchCondition) => {
  const headers: { [id: string]: string } = {
    categories: '類別',
    versions: '版本',
    years: '學年度',
    eduCodes: '學制',
    subjects: '科目',
    grades: '年級',
    fuzzySearch: '搜尋資源類別、知識點或檔名',
    dateFrom: '起始時間',
    dateTo: '結束時間',
    fuzzySearchFields: '搜尋類別',
  };
  return headers[`${id}`] || '-';
};

export const getResourceSearchHeader = (id: keyof TSearchCondition) => {
  const headers: { [id: string]: string } = {
    categories: '類別',
    versions: '版本',
    years: '學年度',
    eduCodes: '學制',
    subjects: '科目',
    grades: '年級（冊次）',
    fuzzySearch: '搜尋資源類別、知識點或檔名',
    dateFrom: '起始時間',
    dateTo: '結束時間',
  };
  return headers[`${id}`] || '-';
};

export const getInternetHeader = (id: keyof TInternetSearchCondition) => {
  const headers: { [id: string]: string } = {
    category: '類別',
    years: '學年度',
    eduCodes: '學制',
    subjects: '科目',
    grades: '年級',
    fuzzySearch: '搜尋資源類別或資源名稱',
    fuzzySearchFields: '搜尋類別',
  };
  return headers[`${id}`] || '-';
};

export const defaultOption = {
  eduCodes: [resourceEduCode.E, resourceEduCode.J, resourceEduCode.H],
  versions: [
    resourceVersion.N,
    resourceVersion.H,
    resourceVersion.K,
    resourceVersion.HE,
  ],
  years: ['110上', '111下'],
  category: [internetCategory.PUBLIC, internetCategory.SCHOOL], // for internet
  categories: [resourceCategory.CRAM_SCHOOL, resourceCategory.SCHOOL],
  grades: ['1', '2', '3', '4', '5'],
  // {
  //   [resourceEduCode.E]: [1, 2, 3, 4, 5, 6],
  //   [resourceEduCode.J]: [1, 2, 3],
  //   [resourceEduCode.H]: [1, 2, 3],
  // },
  subjects: ['國文', '國語', '社會', '自然'],
  resources1: [],
  resources2: [], // for internet
};

export const sendChannel = {
  openExternal: 'openExternal',
  download: 'download',
  pause: 'pause',
  resume: 'resume',
  cancel: 'cancel',
  deleteFile: 'deleteFile',
  extract: 'extract',
  openPath: 'openPath',
  multipleFileDownload: 'multipleFileDownload',
  update: 'update',
  multipleFileUpdate: 'multipleFileUpdate',
  multipleFilePause: 'multipleFilePause',
  multipleFileResume: 'multipleFileResume',
  openOneBook: 'openOneBook',
  openFlash: 'openFlash',
  openTiku: 'openTiku',
  deleteDiskFile: 'deleteDiskFile',
  beginDownload: 'beginDownload',
  beginInstall: 'beginInstall',
  getDisks: 'getDisks',
  dialogMessage: 'dialogMessage',
} as const;

export const onChannel = {
  getBaseDownloadPathSetting: 'getBaseDownloadPathSetting',
  downloadProgressing: 'downloadProgressing',
  downloadListChange: 'downloadListChange',
  reloadMyCDCase: 'reloadMyCDCase',
  oneBookFilesChange: 'oneBookFilesChange',
  tikuFilesChange: 'tikuFilesChange',
  flashFilesChange: 'flashFilesChange',
  fileCatalogChange: 'fileCatalogChange',
  console: 'console',
  updateMsg: 'updateMsg',
  downloadProgress: 'downloadProgress',
  filePrepareListener: 'filePrepareListener',
} as const;

export const invokeChannel = {
  setBaseDownloadPath: 'setBaseDownloadPath',
  setToken: 'setToken',
  getRememberedUsername: 'getRememberedUsername',
  setRememberedUsername: 'setRememberedUsername',
  deleteRememberedUsername: 'deleteRememberedUsername',
  deleteToken: 'deleteToken',
  getDownloadList: 'getDownloadList',
  getToken: 'getToken',
  getOneBookFiles: 'getOneBookFiles',
  getTikuFiles: 'getTikuFiles',
  getFlashFiles: 'getFlashFiles',
  getFileCatalog: 'getFileCatalog',
  getDisks: 'getDisks',
  updateHandle: 'updateHandle',
  getVersion: 'getVersion',
  getConnectivityState: 'getConnectivityState',
  retryConnection: 'retryConnection',
  clearOfflineDisplayName: 'clearOfflineDisplayName',
  setOfflineDisplayName: 'setOfflineDisplayName',
} as const;

// export const updateElectronMessage = {
//   [UpdateElectronStatus.error]: {
//     message: '更新錯誤',
//     state: UpdateElectronStatus.error,
//   },
//   [UpdateElectronStatus.checking]: {
//     message: '檢查更新中',
//     state: UpdateElectronStatus.checking,
//   },
//   [UpdateElectronStatus.updateAvailable]: {
//     message:
//       '有最新版本，請點「確認」進行程式更新。或至 NaniBox3 網頁下載「Windows電腦版」程式並安裝。NaniBox3 網址：https://nanibox3.oneclass.com.tw',
//     state: UpdateElectronStatus.updateAvailable,
//   },
//   [UpdateElectronStatus.downloading]: {
//     message: '下載中',
//     state: UpdateElectronStatus.downloading,
//   },
//   [UpdateElectronStatus.downloadCompleted]: {
//     message: '下載完成',
//     state: UpdateElectronStatus.downloadCompleted,
//   },
//   [UpdateElectronStatus.updateNotAvailable]: {
//     message: '目前已是最新版',
//     state: UpdateElectronStatus.updateNotAvailable,
//   },
//   [UpdateElectronStatus.updated]: {
//     message: '安裝包已下載完成, 是否開始安裝？',
//     state: UpdateElectronStatus.updated,
//   },
// } as const;

export const gradesChangeText = {
  '1': '一年級',
  '2': '二年級',
  '3': '三年級',
  '4': '四年級',
  '5': '五年級',
  '6': '六年級',
};

export const fuzzySearchFieldsText: Record<string, string> = {
  resources1: '資源類別1',
  resources2: '資源類別2',
  resources3: '資源類別3',
  chapterKeyword: '章節關鍵字',
  fileName: '檔名',
  displayName: '顯示名稱',
  urlName: '資源名稱',
};

export const fuzzySearchFieldsOption: Array<
  keyof typeof fuzzySearchFieldsText
> = [
  'resources1',
  'resources2',
  'resources3',
  'chapterKeyword',
  'displayName',
  'fileName',
];

export const internetFuzzySearchFieldsOption: Array<
  keyof typeof fuzzySearchFieldsText
> = ['resources1', 'resources2', 'urlName'];
